
import React, { useState } from 'react';
import { Ban, Unlock, Zap, RotateCcw } from 'lucide-react';
import { Bike } from '@/services/types';
import { BulkAction } from '@/components/DataTable';
import { BulkActionConfirmDialog } from './BulkActionConfirmDialog';
import { SpeedLimitDialog } from './SpeedLimitDialog';
import { toast } from '@/components/ui/sonner';

interface BulkBikeOperationsProps {
  onBulkBlock: (bikes: Bike[]) => Promise<void>;
  onBulkUnblock: (bikes: Bike[]) => Promise<void>;
  onBulkSetSpeedLimit: (bikes: Bike[], speedLimit: number) => Promise<void>;
  onBulkResetRestrictions: (bikes: Bike[]) => Promise<void>;
}

export const useBulkBikeActions = ({
  onBulkBlock,
  onBulkUnblock,
  onBulkSetSpeedLimit,
  onBulkResetRestrictions
}: BulkBikeOperationsProps): {
  actions: BulkAction<Bike>[],
  dialogs: React.ReactNode
} => {
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    actionType: 'block' | 'unblock' | 'speed-limit' | 'reset';
    bikes: Bike[];
    speedLimit?: number;
  }>({
    open: false,
    actionType: 'block',
    bikes: []
  });

  const [speedLimitDialog, setSpeedLimitDialog] = useState<{
    open: boolean;
    bikes: Bike[];
  }>({
    open: false,
    bikes: []
  });

  const validateAction = (actionType: string, bikes: Bike[]): string | null => {
    switch (actionType) {
      case 'block':
        const alreadyBlocked = bikes.filter(bike => bike.movement_blocked);
        if (alreadyBlocked.length === bikes.length) {
          return 'Все выбранные велосипеды уже заблокированы';
        }
        if (alreadyBlocked.length > 0) {
          toast.info(`${alreadyBlocked.length} из ${bikes.length} велосипедов уже заблокированы`);
        }
        break;
      
      case 'unblock':
        const notBlocked = bikes.filter(bike => !bike.movement_blocked);
        if (notBlocked.length === bikes.length) {
          return 'Все выбранные велосипеды уже разблокированы';
        }
        if (notBlocked.length > 0) {
          toast.info(`${notBlocked.length} из ${bikes.length} велосипедов уже разблокированы`);
        }
        break;
      
      case 'reset':
        const noRestrictions = bikes.filter(bike => !bike.movement_blocked && !bike.speed_limit);
        if (noRestrictions.length === bikes.length) {
          return 'У выбранных велосипедов нет активных ограничений';
        }
        break;
    }
    
    return null;
  };

  const handleAction = async (actionType: 'block' | 'unblock' | 'reset', bikes: Bike[]) => {
    const validationError = validateAction(actionType, bikes);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setConfirmDialog({
      open: true,
      actionType,
      bikes
    });
  };

  const handleSpeedLimit = async (bikes: Bike[]) => {
    if (bikes.length === 0) {
      toast.error('Выберите велосипеды для установки ограничения скорости');
      return;
    }

    setSpeedLimitDialog({
      open: true,
      bikes
    });
  };

  const executeAction = async () => {
    try {
      switch (confirmDialog.actionType) {
        case 'block':
          await onBulkBlock(confirmDialog.bikes);
          break;
        case 'unblock':
          await onBulkUnblock(confirmDialog.bikes);
          break;
        case 'speed-limit':
          if (confirmDialog.speedLimit) {
            await onBulkSetSpeedLimit(confirmDialog.bikes, confirmDialog.speedLimit);
          }
          break;
        case 'reset':
          await onBulkResetRestrictions(confirmDialog.bikes);
          break;
      }
    } catch (error) {
      console.error('Ошибка выполнения массовой операции:', error);
      toast.error('Произошла ошибка при выполнении операции');
    } finally {
      setConfirmDialog(prev => ({ ...prev, open: false }));
    }
  };

  const handleSpeedLimitConfirm = (speedLimit: number) => {
    setConfirmDialog({
      open: true,
      actionType: 'speed-limit',
      bikes: speedLimitDialog.bikes,
      speedLimit
    });
    setSpeedLimitDialog(prev => ({ ...prev, open: false }));
  };

  const actions: BulkAction<Bike>[] = [
    {
      id: 'block',
      label: 'Заблокировать',
      icon: <Ban className="h-4 w-4" />,
      variant: 'destructive',
      action: (bikes) => handleAction('block', bikes),
      disabled: (bikes) => bikes.length === 0 || bikes.every(bike => bike.movement_blocked)
    },
    {
      id: 'unblock',
      label: 'Разблокировать',
      icon: <Unlock className="h-4 w-4" />,
      variant: 'default',
      action: (bikes) => handleAction('unblock', bikes),
      disabled: (bikes) => bikes.length === 0 || bikes.every(bike => !bike.movement_blocked)
    },
    {
      id: 'speed-limit',
      label: 'Ограничить скорость',
      icon: <Zap className="h-4 w-4" />,
      variant: 'outline',
      action: handleSpeedLimit,
      disabled: (bikes) => bikes.length === 0
    },
    {
      id: 'reset',
      label: 'Сбросить ограничения',
      icon: <RotateCcw className="h-4 w-4" />,
      variant: 'outline',
      action: (bikes) => handleAction('reset', bikes),
      disabled: (bikes) => bikes.length === 0 || bikes.every(bike => !bike.movement_blocked && !bike.speed_limit)
    }
  ];

  const dialogs = (
    <>
      <BulkActionConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}
        actionType={confirmDialog.actionType}
        selectedBikes={confirmDialog.bikes}
        onConfirm={executeAction}
        speedLimit={confirmDialog.speedLimit}
      />
      <SpeedLimitDialog
        open={speedLimitDialog.open}
        onOpenChange={(open) => setSpeedLimitDialog(prev => ({ ...prev, open }))}
        selectedBikes={speedLimitDialog.bikes}
        onConfirm={handleSpeedLimitConfirm}
      />
    </>
  );

  return { actions, dialogs };
};
